import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { isBooleanLikeTrue } from '@app/helpers/form.helper';
import { LegalEntityFormGroup } from '@app/models/form.models';
import {
  LegalEntityProjectRequest,
  LegalEntityRequest,
  PersonProjectRequest,
  ProjectSetupGRequest,
  ProjectSetupGResponse,
} from '@app/models/project-setup.models';
import { LegalEntityWithRole } from '@app/models/project.models';
import { LegalEntityRole } from '@app/models/shared.enums';
import {
  ApplicableInterestRatesFormGroup,
  getApplicableInterestRatesFormGroup,
  getApplicableInterestRatesFormValueFromProjectInterest,
  getProjectInterestFromApplicableInterestRatesFormValue,
} from '../../applicable-interest-rates/applicable-interest-rates-form.helper';
import {
  GuarantorFormGroup,
  getGuarantorFormGroup,
  getGuarantorFormGroupFromPersonProjectRequest,
  getPersonProjectRequestFromGuarantorFormValue,
} from '../../guarantor-form/guarantor-form.helper';
import { getLegalEntitiyData } from '../../legal-person-validated-form/legal-person-validated-form.helper';
import {
  NotCompletedLoanFormGroup,
  getNotCompletedLoanFormGroupFromFinancialAsset,
} from '../../not-completed-loan-form/not-completed-loan-form.helper';

/** Section G legal entity entry: role + legal entity form (for collateral/guarantee providers). */
export interface LegalEntityWithRoleFormGroup {
  connectionTypeCode: FormControl<LegalEntityRole | null>;
  legalEntity: FormGroup<LegalEntityFormGroup>;
}

export interface SectionGFormGroup {
  loanCoverage: FormControl<number | null>;
  loanUsage: FormControl<number | null>;
  loanTerm: FormControl<number | null>;
  loanInterestRatesType: FormControl<number | null>;
  loanInterestPaymentType: FormControl<number | null>;
  loanLengthMonths: FormControl<number | null>;
  conditions: FormControl<string | null>;

  loanInterests: FormArray<FormGroup<ApplicableInterestRatesFormGroup>>;

  additionalAssurance: FormControl<boolean>;
  collateralProviders: FormArray<FormGroup<LegalEntityWithRoleFormGroup>>;
  collateralInfo: FormControl<string | null>;
  additionalGuarantee: FormControl<boolean>;
  guarantors: FormArray<FormGroup<GuarantorFormGroup>>;
  suretyInfo: FormControl<string | null>;
  otherGuarantee: FormControl<boolean>;
  guarateeProviders: FormArray<FormGroup<LegalEntityWithRoleFormGroup>>;
  guaranteeInfo: FormControl<string | null>;
  capitalRepayment: FormControl<number | null>;
  interestRepayment: FormControl<number | null>;
  notCompletedProjectWithinFiveYears: FormControl<boolean>;
  notCompletedLoans: FormArray<FormGroup<NotCompletedLoanFormGroup>>;
  notCompletedInfo: FormControl<string | null>;
  loanHandlingInfo: FormControl<string | null>;
}

export function getLegalEntityWithRoleFormGroup(
  fb: FormBuilder,
  connectionTypeCode: LegalEntityRole | null = null,
): FormGroup<LegalEntityWithRoleFormGroup> {
  return fb.nonNullable.group({
    connectionTypeCode: fb.control<LegalEntityRole | null>(connectionTypeCode),
    legalEntity: getLegalEntitiyData(fb),
  });
}

function legalEntityFormToRequest(
  legalEntity: FormGroup<LegalEntityFormGroup>,
): LegalEntityRequest {
  const raw = legalEntity.getRawValue();
  return {
    taxNumber: raw.taxNumber ?? '',
    name: raw.name ?? '',
    email: raw.email ?? '',
    country: raw.country ?? '',
    ownershipStructure: raw.ownershipStructure ?? null,
    contactInfo: raw.contactInfo ?? null,
    website: raw.website ?? null,
    teaorNumbers: raw.teaorNumbers ?? null,
    bankAccountNumber: raw.bankAccountNumber ?? null,
    companyTypeId: raw.companyTypeId ?? null,
    address: {
      countryCode: raw.address?.countryCode ?? null,
      zipCode: raw.address?.zipCode ?? null,
      city: raw.address?.city ?? null,
      publicSpace: raw.address?.publicSpace ?? null,
      publicSpaceType: raw.address?.publicSpaceType ?? null,
      houseNumber: raw.address?.houseNumber ?? null,
      doorOrFloor: raw.address?.doorOrFloor ?? null,
    },
  };
}

export function getLegalEntityProjectRequestFromLegalEntityWithRoleFormValue(
  form: FormGroup<LegalEntityWithRoleFormGroup>,
): LegalEntityProjectRequest {
  return {
    connectionTypeCode: form.controls.connectionTypeCode.value,
    legalEntity: legalEntityFormToRequest(form.controls.legalEntity),
  };
}

export function getLegalEntityWithRoleFormGroupFromLegalEntityWithRole(
  fb: FormBuilder,
  entity: LegalEntityWithRole,
  connectionTypeCode: LegalEntityRole,
): FormGroup<LegalEntityWithRoleFormGroup> {
  const group = getLegalEntityWithRoleFormGroup(fb, connectionTypeCode);
  const addr = entity.address;
  group.controls.legalEntity.patchValue({
    name: entity.name,
    taxNumber: entity.taxNumber,
    email: entity.email,
    country: entity.country,
    address: {
      countryCode: entity.country ?? addr?.countryCode ?? '',
      zipCode: addr?.zipCode ?? '',
      city: addr?.city ?? '',
      publicSpace: addr?.publicSpace ?? '',
      publicSpaceType: addr?.publicSpaceType ?? '',
      houseNumber: addr?.houseNumber ?? '',
      doorOrFloor: addr?.doorOrFloor ?? '',
    },
  });
  return group;
}

export function getSectionGFormGroup(
  fb: FormBuilder,
): FormGroup<SectionGFormGroup> {
  return fb.nonNullable.group({
    loanCoverage: fb.control<number | null>(null),
    loanUsage: fb.control<number | null>(null),
    loanTerm: fb.control<number | null>(null),
    loanInterestRatesType: fb.control<number | null>(null),
    loanInterestPaymentType: fb.control<number | null>(null),
    loanLengthMonths: fb.control<number | null>(null),
    conditions: fb.control<string | null>(null),
    loanInterests: fb.nonNullable.array([
      getApplicableInterestRatesFormGroup(fb),
    ]),
    additionalAssurance: fb.nonNullable.control<boolean>(false),
    collateralProviders: fb.nonNullable.array<
      FormGroup<LegalEntityWithRoleFormGroup>
    >([]),
    collateralInfo: fb.control<string | null>(null),
    additionalGuarantee: fb.nonNullable.control<boolean>(false),
    guarantors: fb.nonNullable.array<FormGroup<GuarantorFormGroup>>([
      getGuarantorFormGroup(fb, 'ProjectGuarantor'),
    ]),
    suretyInfo: fb.control<string | null>(null),
    otherGuarantee: fb.nonNullable.control<boolean>(false),
    guarateeProviders: fb.nonNullable.array<
      FormGroup<LegalEntityWithRoleFormGroup>
    >([]),
    guaranteeInfo: fb.control<string | null>(null),
    capitalRepayment: fb.control<number | null>(null),
    interestRepayment: fb.control<number | null>(null),
    notCompletedProjectWithinFiveYears: fb.nonNullable.control<boolean>(false),
    notCompletedLoans: fb.nonNullable.array<
      FormGroup<NotCompletedLoanFormGroup>
    >([]),
    notCompletedInfo: fb.control<string | null>(null),
    loanHandlingInfo: fb.control<string | null>(null),
  });
}

function getLegalEntitiesFromSectionGForm(
  formGroup: FormGroup<SectionGFormGroup>,
): LegalEntityProjectRequest[] {
  const legalEntities: LegalEntityProjectRequest[] = [];
  if (isBooleanLikeTrue(formGroup.controls.additionalAssurance.value)) {
    formGroup.controls.collateralProviders.controls.forEach(
      (providerForm: FormGroup<LegalEntityWithRoleFormGroup>) => {
        legalEntities.push(
          getLegalEntityProjectRequestFromLegalEntityWithRoleFormValue(
            providerForm,
          ),
        );
      },
    );
  }
  if (isBooleanLikeTrue(formGroup.controls.otherGuarantee.value)) {
    formGroup.controls.guarateeProviders.controls.forEach(
      (providerForm: FormGroup<LegalEntityWithRoleFormGroup>) => {
        legalEntities.push(
          getLegalEntityProjectRequestFromLegalEntityWithRoleFormValue(
            providerForm,
          ),
        );
      },
    );
  }
  return legalEntities;
}

function getPersonProjectRequestsFromSectionGForm(
  formGroup: FormGroup<SectionGFormGroup>,
): PersonProjectRequest[] {
  if (!isBooleanLikeTrue(formGroup.controls.additionalGuarantee.value)) {
    return [];
  }
  const personProjectRequests: PersonProjectRequest[] = [];
  formGroup.controls.guarantors.controls.forEach(
    (guarantorForm: FormGroup<GuarantorFormGroup>) => {
      personProjectRequests.push(
        getPersonProjectRequestFromGuarantorFormValue(guarantorForm),
      );
    },
  );
  return personProjectRequests;
}

export function getSectionGUpdateData(
  formGroup: FormGroup<SectionGFormGroup>,
): ProjectSetupGRequest {
  return {
    loanCoverage: formGroup.controls.loanCoverage.value!,
    loanUsage: formGroup.controls.loanUsage.value!,
    loanTerm: formGroup.controls.loanTerm.value!,
    loanInterestRatesType: formGroup.controls.loanInterestRatesType.value!,
    loanInterestPaymentType: formGroup.controls.loanInterestPaymentType.value!,
    loanLengthMonths: formGroup.controls.loanLengthMonths.value!,
    conditions: formGroup.controls.conditions.value!,
    loanInterests: {
      interest: formGroup.controls.loanInterests.controls.map(
        (applicableInterestRatesForm) =>
          getProjectInterestFromApplicableInterestRatesFormValue(
            applicableInterestRatesForm,
          ),
      ),
    },
    informationOnNatureAndConditionsOfWarranty: isBooleanLikeTrue(
      formGroup.controls.additionalAssurance.value,
    )
      ? formGroup.controls.collateralInfo.value
      : null,
    informationOnNatureAndConditionsOfSurety: isBooleanLikeTrue(
      formGroup.controls.additionalGuarantee.value,
    )
      ? formGroup.controls.suretyInfo.value
      : null,
    informationOnNatureAndConditionsOfGuarantee: isBooleanLikeTrue(
      formGroup.controls.otherGuarantee.value,
    )
      ? formGroup.controls.guaranteeInfo.value
      : null,
    loanTypeCapitalPaymentFrequency: formGroup.controls.capitalRepayment.value,
    loanTypeInterestPaymentFrequency:
      formGroup.controls.interestRepayment.value,
    informationOnLoanAndNonCompliance: isBooleanLikeTrue(
      formGroup.controls.notCompletedProjectWithinFiveYears.value,
    )
      ? formGroup.controls.notCompletedInfo.value
      : null,
    informationOnLoanAndItsManagement:
      formGroup.controls.loanHandlingInfo.value,
    persons: getPersonProjectRequestsFromSectionGForm(formGroup),
    legalEntities: getLegalEntitiesFromSectionGForm(formGroup),
  };
}

function getGuarantorsFromPersonProjectRequest(
  fb: FormBuilder,
  personProjectRequests: PersonProjectRequest[],
): FormArray<FormGroup<GuarantorFormGroup>> {
  const guarantors: FormArray<FormGroup<GuarantorFormGroup>> = fb.array<
    FormGroup<GuarantorFormGroup>
  >([]);

  if (personProjectRequests.length > 0) {
    personProjectRequests.forEach((personProjectRequest) => {
      guarantors.push(
        getGuarantorFormGroupFromPersonProjectRequest(
          fb,
          personProjectRequest,
          'ProjectGuarantor',
        ),
      );
    });
  } else {
    guarantors.push(getGuarantorFormGroup(fb, 'ProjectGuarantor'));
  }
  return guarantors;
}

/**
 * Patches the Section G form with the GET response.
 *
 * Schema gaps (PUT/GET): The API schema does not include:
 * - defaultedLoans / notCompletedLoans: array of { lender?, amount?, expiryDate?, reason? } for "Nem teljesített hitel".
 * - Explicit booleans for "További biztosítéknyűjtés", "További kezességvállalás", "További garancia" (we infer from array length).
 * - notCompletedProjectWithinFiveYears: we default to false when patching; backend may add this later.
 */
export function patchSectionGFormWithProjectSetupGResponse(
  formGroup: FormGroup<SectionGFormGroup>,
  response: ProjectSetupGResponse,
  fb: FormBuilder,
): void {
  const loanInterestsArray = formGroup.controls.loanInterests;
  loanInterestsArray.clear();
  const interestRates = response.interestRates?.interest ?? [];
  if (interestRates.length === 0) {
    loanInterestsArray.push(getApplicableInterestRatesFormGroup(fb));
  } else {
    interestRates.forEach((rate) => {
      const group = getApplicableInterestRatesFormGroup(fb);
      group.patchValue(
        getApplicableInterestRatesFormValueFromProjectInterest(rate),
      );
      loanInterestsArray.push(group);
    });
  }

  const collateralArray = formGroup.controls.collateralProviders;
  collateralArray.clear();
  const collateralEntities = (response.legalEntitySpecialPurpose ?? []).filter(
    (le) => le.role?.code === 'ProjectGuarantor',
  );
  collateralEntities.forEach((entity) => {
    collateralArray.push(
      getLegalEntityWithRoleFormGroupFromLegalEntityWithRole(
        fb,
        entity,
        'ProjectGuarantor',
      ),
    );
  });

  const guarantorsArray = formGroup.controls.guarantors;
  guarantorsArray.clear();
  const persons = response.persons ?? [];
  if (persons.length === 0) {
    guarantorsArray.push(getGuarantorFormGroup(fb, 'ProjectGuarantor'));
  } else {
    persons.forEach((personRequest) => {
      guarantorsArray.push(
        getGuarantorFormGroupFromPersonProjectRequest(
          fb,
          personRequest,
          'ProjectGuarantor',
        ),
      );
    });
  }

  const guarateeArray = formGroup.controls.guarateeProviders;
  guarateeArray.clear();
  const guaranteeEntities = (response.legalEntitySpecialPurpose ?? []).filter(
    (le) => le.role?.code === 'ProjectWarranty',
  );
  guaranteeEntities.forEach((entity) => {
    guarateeArray.push(
      getLegalEntityWithRoleFormGroupFromLegalEntityWithRole(
        fb,
        entity,
        'ProjectWarranty',
      ),
    );
  });

  const notCompletedArray = formGroup.controls.notCompletedLoans;
  notCompletedArray.clear();
  const sectionGAssets = response.financialAssetsSectionG ?? [];
  if (sectionGAssets.length > 0) {
    sectionGAssets.forEach((asset) => {
      notCompletedArray.push(
        getNotCompletedLoanFormGroupFromFinancialAsset(fb, asset),
      );
    });
  }

  formGroup.patchValue({
    loanCoverage: response.loanCoverage,
    loanUsage: response.loanUsage,
    loanTerm: response.loanTerm,
    loanInterestRatesType: response.loanInterestRatesType,
    loanInterestPaymentType: response.loanInterestPaymentType,
    loanLengthMonths: response.loanLengthMonths,
    conditions: response.conditions,
    additionalAssurance: collateralEntities.length > 0,
    collateralInfo: response.informationOnNatureAndConditionsOfWarranty,
    additionalGuarantee: persons.length > 0,
    suretyInfo: response.informationOnNatureAndConditionsOfSurety,
    otherGuarantee: guaranteeEntities.length > 0,
    guaranteeInfo: response.informationOnNatureAndConditionsOfGuarantee,
    capitalRepayment: response.loanTypeCapitalPaymentFrequency,
    interestRepayment: response.loanTypeInterestPaymentFrequency,
    notCompletedProjectWithinFiveYears: false,
    notCompletedInfo: response.informationOnLoanAndNonCompliance,
    loanHandlingInfo: response.informationOnLoanAndItsManagement,
  });
}
